import React, { useState, type ReactNode } from 'react';
import type { FieldSchema, Site } from '@/types';
import { ic, sized } from '@/components/Icons';
import { DataTable, FieldRow, Modal, Pagination, ProgressBar, Section, type Column } from '@/components/ui';
import { ds } from '@/data';
import { useI18n } from '@/i18n';
import { progresoSite } from '@/lib/progresoPortal';
import type { EntidadNA } from '../../../db/columns';
import { paginate, sortRows, useTableState, type SortMap } from '@/hooks/useTableState';
import { useGo } from '@/hooks/useRoute';

/* ---------- Section block (with state tone) ---------- */

export interface SectionBlockProps {
  sectionKey: string;
  fields: FieldSchema;
  draft: Record<string, string | string[] | undefined>;
  onChange: (k: string, v: string | string[]) => void;
  editing: boolean;
  tone?: string;
  defaultOpen?: boolean;
  right?: ReactNode;
  /** La ficha entera, para las reglas que miran otro campo. */
  record?: Record<string, unknown> | null;
  /** De qué ficha son estos campos, para poder marcarlos «no aplica». */
  entidad?: EntidadNA;
  id?: string;
  onExcusar?: () => void;
}

export function SectionBlock({
  sectionKey, fields, draft, onChange, editing, tone, defaultOpen = true, right,
  record, entidad, id, onExcusar,
}: SectionBlockProps) {
  const { t } = useI18n();
  const fld = fields.filter((f) => f.section === sectionKey);
  return (
    <Section title={t(`sec.${sectionKey}`)} tone={tone} right={right} defaultOpen={defaultOpen}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 1, marginTop: 8 }}>
        {fld.map((f) => (
          <FieldRow
            key={f.key}
            field={f}
            value={draft[f.key]}
            onChange={onChange}
            editing={editing}
            record={record}
            entidad={entidad}
            id={id}
            onExcusar={onExcusar}
          />
        ))}
      </div>
    </Section>
  );
}

/* ---------- Legal contact change request ---------- */

/** Lo mismo que acepta el servidor. Comprobarlo aquí evita subir diez
 *  megas para que los rechace al final. */
const MAX_CARTA = 10 * 1024 * 1024;

export interface LegalRequestDraft {
  first: string;
  last: string;
  email: string;
  phone: string;
  /** El archivo en sí, no su nombre: se sube al portal. */
  file: File | null;
}

export function LegalChangeModal({
  open, onClose, onSend, orgName,
}: {
  open: boolean;
  onClose: () => void;
  /** Envía la solicitud. Lanza si el servidor la rechaza. */
  onSend: (draft: LegalRequestDraft) => Promise<void>;
  orgName?: string;
}) {
  const { lang, t } = useI18n();
  const vacio: LegalRequestDraft = { first: '', last: '', email: '', phone: '', file: null };
  const [f, setF] = useState<LegalRequestDraft>(vacio);
  const [enviando, setEnviando] = useState(false);
  const [fallo, setFallo] = useState('');
  const [tocado, setTocado] = useState(false);
  const [archivoMal, setArchivoMal] = useState('');

  React.useEffect(() => {
    if (open) { setF(vacio); setFallo(''); setTocado(false); setArchivoMal(''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  type CampoTexto = 'first' | 'last' | 'email' | 'phone';
  const fields: { k: CampoTexto; l: string; span?: number }[] = [
    { k: 'first', l: t('legal.new_first') },
    { k: 'last', l: t('legal.new_last') },
    { k: 'email', l: t('legal.new_email'), span: 2 },
    { k: 'phone', l: t('legal.new_phone'), span: 2 },
  ];

  // El correo lo valida el servidor de todos modos, pero enterarse allí es
  // tarde: el formulario ya se había cerrado y lo escrito se perdía. Aquí
  // se comprueba antes de enviar y el error sale junto al campo.
  const correoOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim());
  const faltan: CampoTexto[] = [];
  if (!f.first.trim()) faltan.push('first');
  if (!f.last.trim()) faltan.push('last');
  if (!correoOk) faltan.push('email');
  const listo = faltan.length === 0;

  async function enviar() {
    setTocado(true);
    if (!listo) return;
    setEnviando(true);
    setFallo('');
    try {
      await onSend({ ...f, email: f.email.trim() });
      onClose();
    } catch (err) {
      // NO se cierra: lo escrito sigue ahí para corregirlo
      setFallo(err instanceof Error ? err.message : String(err));
    } finally {
      setEnviando(false);
    }
  }

  const malo = (k: CampoTexto) => tocado && faltan.includes(k);

  return (
    <Modal
      open={open}
      onClose={enviando ? () => {} : onClose}
      title={t('legal.request_title')}
      width={520}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={enviando}>{t('common.cancel')}</button>
          <button className="btn primary" onClick={() => void enviar()} disabled={enviando || (tocado && !listo)}>
            {enviando ? (lang === 'es' ? 'Enviando…' : 'Sending…') : t('common.submit')}
          </button>
        </>
      }
    >
      <p style={{ marginTop: 0, color: 'var(--text-2)', fontSize: 13 }}>
        {t('legal.request_help')}
        {orgName ? <> <strong>{orgName}</strong>.</> : null}
      </p>

      {fallo ? (
        <div
          role="alert"
          style={{
            display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12, padding: '10px 12px',
            border: '1px solid var(--err)', borderRadius: 8, background: 'var(--st-req-l)',
            fontSize: 12.5, color: 'var(--err)',
          }}
        >
          <span style={{ flexShrink: 0, display: 'inline-flex' }}>{ic.warn}</span>
          <span>{fallo}</span>
        </div>
      ) : null}

      <div className="m-grid1" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {fields.map((it) => (
          <label key={it.k} style={{ display: 'flex', flexDirection: 'column', gap: 4, gridColumn: it.span === 2 ? 'span 2' : 'auto' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)' }}>
              {it.l}
              {it.k !== 'phone' ? <span style={{ color: 'var(--err)' }}> *</span> : null}
            </span>
            <input
              value={f[it.k]}
              disabled={enviando}
              placeholder={it.k === 'email' ? 'nombre@dominio.com' : undefined}
              onChange={(e) => {
                const v = e.target.value;
                setF((prev) => ({ ...prev, [it.k]: v }));
              }}
              style={{
                padding: '8px 10px', borderRadius: 6, fontSize: 13,
                border: '1px solid ' + (malo(it.k) ? 'var(--err)' : 'var(--line-2)'),
              }}
            />
            {malo(it.k) ? (
              <span style={{ fontSize: 11, color: 'var(--err)' }}>
                {it.k === 'email'
                  ? (lang === 'es' ? 'Escribe un correo válido, con @ y dominio.' : 'Enter a valid email address.')
                  : (lang === 'es' ? 'Obligatorio.' : 'Required.')}
              </span>
            ) : null}
          </label>
        ))}
      </div>
      <div style={{ marginTop: 12 }}>
        <label className="btn" style={{ width: '100%', justifyContent: 'flex-start' }}>
          {ic.upload}
          <span>{f.file ? f.file.name : t('legal.attach_doc')}</span>
          <input
            type="file"
            accept=".pdf,.docx"
            style={{ display: 'none' }}
            disabled={enviando}
            onChange={(e) => {
              const elegido = e.target.files?.[0] ?? null;
              setArchivoMal('');
              if (elegido && !/\.(pdf|docx)$/i.test(elegido.name)) {
                setArchivoMal(lang === 'es'
                  ? 'La carta tiene que ser un PDF o un DOCX.'
                  : 'The letter must be a PDF or a DOCX.');
              } else if (elegido && elegido.size > MAX_CARTA) {
                setArchivoMal(lang === 'es'
                  ? 'La carta pasa de 10 MB.'
                  : 'The letter is over 10 MB.');
              } else {
                setF((prev) => ({ ...prev, file: elegido }));
              }
              // permite volver a elegir el mismo archivo tras corregirlo
              e.target.value = '';
            }}
          />
        </label>
        {f.file ? (
          <button
            className="btn"
            style={{ marginTop: 6, fontSize: 12 }}
            disabled={enviando}
            onClick={() => setF((prev) => ({ ...prev, file: null }))}
          >
            {lang === 'es' ? 'Quitar el archivo' : 'Remove file'}
          </button>
        ) : null}
        {archivoMal ? (
          <div role="alert" style={{ fontSize: 11.5, color: 'var(--err)', marginTop: 6 }}>
            {archivoMal}
          </div>
        ) : null}
        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>
          {lang === 'es'
            ? 'Opcional. PDF o DOCX, hasta 10 MB. Se guarda en el portal y solo la ven ETC y quien alcance esta organización.'
            : 'Optional. PDF or DOCX, up to 10 MB. Stored in the portal; visible only to ETC and to people with access to this organization.'}
        </div>
      </div>
    </Modal>
  );
}

/* ---------- Sites nested under an organization ---------- */

type SiteRow = Site & {
  _key: string;
  _pct: number;
  _primary: boolean;
  _instr: number;
};

export function OrgSites({ sites }: { sites: Site[] }) {
  const { lang, t } = useI18n();
  const go = useGo();
  const table = useTableState(10);

  const enriched: SiteRow[] = sites.map((s) => ({
    ...s,
    _key: s.SiteId,
    _pct: progresoSite(s.SiteId).pct,
    _primary: s.SiteId === s.PrimarySiteId,
    _instr: ds.listAssignments({ siteId: s.SiteId }).length,
  }));

  const sortValues: SortMap<SiteRow> = {
    p: { type: 'number', get: (r) => (r._primary ? 1 : 0) },
    SiteName: { type: 'text', get: (r) => r.SiteName },
    SiteCity: { type: 'text', get: (r) => r.SiteCity },
    cp: { type: 'text', get: (r) => r.SitePostalCode || '' },
    ws: { type: 'number', get: (r) => Number(r.Workstations) || 0 },
    instr: { type: 'number', get: (r) => r._instr },
    pct: { type: 'number', get: (r) => r._pct },
  };

  const { rows, page, pageCount } = paginate(sortRows(enriched, table.sort, sortValues), table.page, table.per);

  const columns: Column<SiteRow>[] = [
    {
      key: 'p',
      label: '',
      sortable: true,
      render: (r) =>
        r._primary ? (
          <span data-tip={t('sites.primary_tip')} style={{ color: '#FFB020' }}>
            {React.cloneElement(ic.star, { size: 14, fill: 'currentColor' })}
          </span>
        ) : (
          ''
        ),
    },
    {
      key: 'SiteName',
      label: lang === 'es' ? 'Sitio' : 'Site',
      sortable: true,
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            {r.SiteName}
            {r._primary ? (
              <span data-tip={t('sites.primary_label')} tabIndex={0} style={{ color: '#FFB020', display: 'inline-flex', cursor: 'default' }}>
                {React.cloneElement(ic.star, { size: 13, fill: 'currentColor' })}
              </span>
            ) : null}
          </div>
          <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>{r.SiteId} · ATCCSN {r.ATCCSN}</div>
        </div>
      ),
    },
    { key: 'SiteCity', label: lang === 'es' ? 'Ciudad' : 'City', sortable: true },
    {
      key: 'cp',
      label: <span title={lang === 'es' ? 'Código postal' : 'Postal code'}>CP</span>,
      sortable: true,
      render: (r) =>
        !r.SitePostalCode ? <span className="chip err">—</span>
        : r.SitePostalCode === '0000' ? <span className="chip err mono">{r.SitePostalCode}</span>
        : <span className="mono">{r.SitePostalCode}</span>,
    },
    {
      key: 'ws',
      label: <span title={lang === 'es' ? 'Estaciones de trabajo (equipos)' : 'Workstations'}>WS</span>,
      align: 'right',
      sortable: true,
      render: (r) => (
        <span className="mono" style={{ color: r.Workstations ? 'var(--text)' : 'var(--err)', fontWeight: 600 }}>
          {r.Workstations || '!'}
        </span>
      ),
    },
    { key: 'instr', label: lang === 'es' ? 'Instr.' : 'Instr.', align: 'right', mono: true, sortable: true, render: (r) => r._instr },
    {
      key: 'pct',
      label: t('common.completeness'),
      sortable: true,
      render: (r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
          <div style={{ flex: 1 }}><ProgressBar pct={r._pct} /></div>
          <span className="mono" style={{ fontSize: 11, fontWeight: 700, minWidth: 30, textAlign: 'right', color: r._pct === 100 ? 'var(--ok)' : 'var(--text-2)' }}>
            {r._pct}%
          </span>
        </div>
      ),
    },
    { key: 'go', label: '', align: 'right', render: () => <span style={{ color: 'var(--text-3)' }}>{ic.chevR}</span> },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
        <span style={{ color: 'var(--brand-primary)', display: 'inline-flex' }}>{sized(ic.site, 16)}</span>
        <div style={{ fontWeight: 700, fontSize: 14 }}>{lang === 'es' ? 'Sites de la organización' : 'Organization sites'}</div>
        <span className="chip">{sites.length}</span>
      </div>

      {sites.length === 0 ? (
        <div className="card" style={{ padding: '28px 16px', textAlign: 'center', color: 'var(--text-3)' }}>
          <div style={{ display: 'inline-flex', marginBottom: 8, color: 'var(--line-2)' }}>{sized(ic.site, 28)}</div>
          <div style={{ fontSize: 13 }}>{lang === 'es' ? 'Esta organización aún no tiene sites.' : 'This organization has no sites yet.'}</div>
        </div>
      ) : (
        <>
          <DataTable columns={columns} rows={rows} sort={table.sort} onSort={table.onSort} stickyHeader onRowClick={(s) => go('site', { siteId: s.SiteId })} />
          <Pagination page={page} pageCount={pageCount} onPage={table.setPage} total={sites.length} per={table.per} onPer={table.onPer} perOptions={[5, 10, 25, 50]} />
        </>
      )}
    </div>
  );
}
